import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  FiChevronDown,
  FiX,
  FiMenu,
  FiGrid,
  FiStar,
  FiArrowRight,
  FiZap,
  FiDownload,
  FiHeadphones,
  FiDollarSign,
  FiCpu,
  FiShoppingBag,
} from "react-icons/fi";
import "./Navbar.css";

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [productsOpen, setProductsOpen] = useState(false);
  const [mobileProductsOpen, setMobileProductsOpen] = useState(false);

  const [clickCount, setClickCount] = useState(0);
  const [showPopup, setShowPopup] = useState(false);

  const logoClickTimeout = useRef(null);
  const dropdownRef = useRef(null);
  const location = useLocation();

  /* ── Scroll effect ── */
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  /* ── Close dropdown on outside click ── */
  useEffect(() => {
    const onClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target))
        setProductsOpen(false);
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  /* ── Close everything on route change ── */
  useEffect(() => {
    setMenuOpen(false);
    setProductsOpen(false);
    setMobileProductsOpen(false);
  }, [location]);

  /* ── Lock body scroll when drawer open ── */
  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [menuOpen]);

  /* ── Secret logo click ── */
  const handleLogoClick = () => {
    setClickCount((prev) => {
      const n = prev + 1;
      if (n >= 3) { setShowPopup(true); return 0; }
      clearTimeout(logoClickTimeout.current);
      logoClickTimeout.current = setTimeout(() => setClickCount(0), 2000);
      return n;
    });
  };

  const isActive = (path) =>
    location.pathname === path || location.pathname.toLowerCase().startsWith(path.toLowerCase());

  /* ── Data ── */
  const productItems = [
    {
      icon: <FiCpu size={18} />,
      label: "ERP Software",
      desc: "Core business management platform",
      to: "/erp-software",
    },
    {
      icon: <FiDollarSign size={18} />,
      label: "Billing Software",
      desc: "GST-ready billing and invoicing workflows",
      to: "/billing-software",
    },
    {
      icon: <FiGrid size={18} />,
      label: "Accounting Software",
      desc: "Connected accounting and financial workflows",
      to: "/accounting-software",
    },
    {
      icon: <FiShoppingBag size={18} />,
      label: "Inventory Management",
      desc: "Connected stock and product workflows",
      to: "/inventory-management-software",
    },
    {
      icon: <FiZap size={18} />,
      label: "POS Software",
      desc: "Fast retail billing and checkout workflows",
      to: "/pos-software",
    },
    {
      icon: <FiDollarSign size={18} />,
      label: "GST Software",
      desc: "GST-ready billing and reporting workflows",
      to: "/gst-software",
    },
    {
      icon: <FiGrid size={18} />,
      label: "Features & Solutions",
      desc: "Business software features and connected solutions",
      to: "/features-solutions",
    },
    {
      icon: <FiStar size={18} />,
      label: "Best Business Software",
      desc: "Buyer guide for choosing business software",
      to: "/best-business-software",
    },
    {
      icon: <FiCpu size={18} />,
      label: "Comparisons",
      desc: "Software comparison and buying guide",
      to: "/comparisons",
    },
    {
      icon: <FiShoppingBag size={18} />,
      label: "Business Software India",
      desc: "ERP, billing, POS, inventory and GST for India",
      to: "/india",
    },
    {
      icon: <FiShoppingBag size={18} />,
      label: "Industry Solutions",
      desc: "Business software for different industries",
      to: "/industries",
    },
    {
      icon: <FiGrid size={18} />,
      label: "Product Overview",
      desc: "Complete ERP suite at a glance",
      to: "/Productoverview",
    },
    {
      icon: <FiStar size={18} />,
      label: "What's New",
      desc: "Latest features & release notes",
      to: "/whatsnew",
    },
  ];

  const navLinks = [
    { label: "Pricing", to: "/Pricing", icon: <FiDollarSign size={15} /> },
    { label: "Features", to: "/Features", icon: <FiCpu size={15} /> },
    { label: "Support", to: "/Support", icon: <FiHeadphones size={15} /> },
    { label: "Industries", to: "/Industrial-specification", icon: <FiShoppingBag size={15} /> },
  ];

  const controlList = [
    { name: "Support page → Ctrl + F9", usedIn: "Click to quick links" },
    { name: "Industries specific page → Ctrl + F9", usedIn: "Click to quick links" },
  ];

  return (
    <>
      {/* ─────────── MAIN NAVBAR ─────────── */}
      <nav className={`navbar navbar--no-announce ${scrolled ? "navbar--scrolled" : ""}`}>
        <div className="navbar-inner">

          {/* Logo */}
          <div className="nav-logo" onClick={handleLogoClick}>
            <Link to="/">
              <img src="/images/auditlogo.png" alt="Auditus-iERP Logo" />
            </Link>
          </div>

          {/* ── Desktop links ── */}
          <ul className="nav-links">
            {/* Products mega-dropdown */}
            <li
              className="nav-item has-dropdown"
              ref={dropdownRef}
            >
              <span
                className={`nav-link ${
                  isActive("/erp-software") || isActive("/billing-software") || isActive("/accounting-software") || isActive("/inventory-management-software") || isActive("/pos-software") || isActive("/gst-software") || isActive("/features-solutions") || isActive("/best-business-software") || isActive("/comparisons") || isActive("/india") || isActive("/Productoverview") || isActive("/whatsnew") ? "active" : ""
                }`}
                onClick={() => setProductsOpen((p) => !p)}
                style={{ userSelect: "none" }}
              >
                Products{" "}
                <FiChevronDown
                  className={`nav-chevron ${productsOpen ? "open" : ""}`}
                  size={14}
                />
              </span>

              {/* Mega Dropdown */}
              <div className={`mega-drop ${productsOpen ? "open" : ""}`}>
                <div className="mega-drop-inner">
                  <div className="mega-col">
                    <p className="mega-col-label">ERP Suite</p>
                    <div className="mega-item-grid">
                      {productItems.map((item, i) => (
                        <Link key={i} to={item.to} className="mega-item">
                          <span className="mega-item-icon">{item.icon}</span>
                          <div className="mega-item-text">
                            <span className="mega-item-name">{item.label}</span>
                            <span className="mega-item-desc">{item.desc}</span>
                          </div>
                          <FiArrowRight className="mega-item-arrow" size={14} />
                        </Link>
                      ))}
                    </div>
                  </div>
                  <div className="mega-col mega-promo">
                    <div className="mega-promo-icon"><FiZap size={22} /></div>
                    <p className="mega-promo-title">7-Day Free Trial</p>
                    <p className="mega-promo-desc">
                      Full-featured access. No credit card required. Cancel anytime.
                    </p>
                    <Link to="/Demo" className="mega-promo-btn">
                      Start Free Trial <FiArrowRight size={13} />
                    </Link>
                  </div>
                </div>
              </div>
            </li>

            {navLinks.map((link, i) => (
              <li key={i} className="nav-item">
                <Link
                  to={link.to}
                  className={`nav-link ${isActive(link.to) ? "active" : ""}`}
                >
                  {link.label}
                </Link>
              </li>
            ))}
          </ul>

          {/* ── Desktop CTAs ── */}
          <div className="nav-actions">
            <Link to="/Demo" className="nav-cta nav-cta--ghost">
              Get a Demo
            </Link>
            <Link to="/Download" className="nav-cta nav-cta--primary">
              <FiDownload size={14} /> Download
            </Link>
          </div>

          {/* ── Hamburger ── */}
          <button
            className={`nav-hamburger ${menuOpen ? "is-open" : ""}`}
            onClick={() => setMenuOpen((p) => !p)}
            aria-label="Toggle menu"
          >
            <span />
            <span />
            <span />
          </button>
        </div>
      </nav>

      {/* ─────────── MOBILE OVERLAY ─────────── */}
      <div
        className={`mobile-overlay ${menuOpen ? "open" : ""}`}
        onClick={() => setMenuOpen(false)}
      />

      {/* ─────────── MOBILE DRAWER ─────────── */}
      <div className={`mobile-drawer ${menuOpen ? "open" : ""}`}>
        {/* Drawer header */}
        <div className="drawer-header">
          <img src="/images/auditlogo.png" alt="Auditus-iERP" className="drawer-logo" />
          <button className="drawer-close" onClick={() => setMenuOpen(false)}>
            <FiX size={22} />
          </button>
        </div>

        {/* Drawer nav */}
        <nav className="drawer-nav">
          {/* Products accordion */}
          <button
            className="drawer-link drawer-accordion"
            onClick={() => setMobileProductsOpen((p) => !p)}
          >
            <span>Products</span>
            <FiChevronDown
              size={16}
              className={`nav-chevron ${mobileProductsOpen ? "open" : ""}`}
            />
          </button>
          <div className={`drawer-sub ${mobileProductsOpen ? "open" : ""}`}>
            {productItems.map((item, i) => (
              <Link
                key={i}
                to={item.to}
                className="drawer-sub-link"
                onClick={() => setMenuOpen(false)}
              >
                <span className="drawer-sub-icon">{item.icon}</span>
                {item.label}
              </Link>
            ))}
          </div>

          {navLinks.map((link, i) => (
            <Link
              key={i}
              to={link.to}
              className={`drawer-link ${isActive(link.to) ? "active" : ""}`}
              onClick={() => setMenuOpen(false)}
            >
              <span className="drawer-link-icon">{link.icon}</span>
              {link.label}
            </Link>
          ))}
        </nav>

        {/* Drawer footer CTAs */}
        <div className="drawer-footer">
          <Link
            to="/Demo"
            className="nav-cta nav-cta--ghost full"
            onClick={() => setMenuOpen(false)}
          >
            Get a Demo
          </Link>
          <Link
            to="/Download"
            className="nav-cta nav-cta--primary full"
            onClick={() => setMenuOpen(false)}
          >
            <FiDownload size={14} /> Download
          </Link>
        </div>
      </div>

      {/* ─────────── SECRET POPUP ─────────── */}
      {showPopup && (
        <div className="secret-backdrop" onClick={() => setShowPopup(false)}>
          <div className="secret-modal" onClick={(e) => e.stopPropagation()}>
            <button className="secret-close" onClick={() => setShowPopup(false)}>
              <FiX size={18} />
            </button>
            <h3>Auditus-iERP Quick Links</h3>
            <ul className="control-list">
              {controlList.map((c, i) => (
                <li key={i}>
                  <strong>{c.name}</strong> — {c.usedIn}
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </>
  );
};

export default Navbar;
